import * as d3 from 'd3'

function euclidDistance(x1, y1, x2, y2){
    return Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2);
}

function mindist(x, y, x1, y1, x2, y2){
    var dx1 = x - x1, dx2 = x - x2,
        dy1 = y - y1, dy2 = y - y2;

    if (dx1*dx2 < 0) { // x is between x1 and x2
        if (dy1*dy2 < 0) { // (x,y) is inside the rectangle
            return 0; // return 0 as point is in rect
        }
        return Math.min(Math.pow(Math.abs(dy1),2),Math.pow(Math.abs(dy2),2));
    }
    if (dy1*dy2 < 0) { // y is between y1 and y2
        // we don't have to test for being inside the rectangle, it's already tested.
        return Math.min(Math.pow(Math.abs(dx1),2),Math.pow(Math.abs(dx2),2));
    }
    return Math.min( Math.min(euclidDistance(x,y,x1,y1),euclidDistance(x,y,x2,y2)), Math.min(euclidDistance(x,y,x1,y2),euclidDistance(x,y,x2,y1)) );
}

function knearest(bestqueue, resultqueue, x, y, k, scannedqueue) {
    bestqueue.sort(function(a, b){ // sort children according to their mindist/dist to searchpoint
        [a, b].forEach(function(val){
            if(val.mindist == undefined){ // add minidst to nodes if not there already
                if (scannedqueue) scannedqueue.push(val); // feed the scannedqueue if it is given
                if(!val.length){ // is leaf
                    val.mindist = euclidDistance(x, y, val.data[0], val.data[1]);
                }else{
                    val.mindist = mindist(x, y, val.x1, val.y1, val.x2, val.y2);
                }
            }
        });
        return b.mindist - a.mindist;
    });
    for (var i=bestqueue.length-1; i>=0; i--){ // add nearest leafs if any
        var elem = bestqueue[i];
        if(!elem.length){ // is leaf
            bestqueue.pop();
            resultqueue.push(elem);
            if(resultqueue.length >=k || bestqueue.length == 0){ // return if k neighbors found or no points left
                return;
            }
        }else{
            break;
        }
    }
    var visited = bestqueue.pop();
    for (var i=0; i<visited.length; i++) { // add child quadrants to queue
        if(visited[i]) {
            visited[i].mindist = undefined; // reset before adding it to the queue
            bestqueue.push(visited[i]);
        }
    }
    knearest(bestqueue, resultqueue, x, y, k, scannedqueue); // recursion
}
// collapses a quadtree into a list of nodes, adding their depth
function retrieveNodesAndDepth(quadtree) {
    const nodes = [];
    let maxdepth = 0;
    quadtree.root().depth = 0; // root node depth
    quadtree.visit((node, x1, y1, x2, y2) => {
        node.x1 = x1;
        node.y1 = y1;
        node.x2 = x2;
        node.y2 = y2;
        nodes.push(node);
        for (var i=0; i < node.length; i++) {
            if (node[i]) {
                node[i].depth = node.depth+1;
                maxdepth = (node[i].depth > maxdepth) ? node[i].depth : maxdepth;
            }
        }
        return false;
    });
    return {"nodes": nodes, "depth": maxdepth};
}

export const drawBivariatePlot = props => {
    const { data, width, height } = props
    const { coordinates, locations, k, zip, tab } = data
    const chart = selection => {
        if (!coordinates || coordinates.length === 0) { return }
        const quadtree = d3.quadtree()
            .extent([[-1,-1], [width +1, height +1]])
            .addAll(coordinates)
        const { nodes, depth } = retrieveNodesAndDepth(quadtree)
        const color = d3.scaleLinear()
            .domain([0, depth])
            .range(['#fff', '#555'])
        const x = d3.scaleLinear().domain(d3.extent(coordinates, d => d[0])).range([0, width])
        const y = d3.scaleLinear().domain(d3.extent(coordinates, d => d[1])).range([0, height])
        const colorCluster = d3.scaleOrdinal(
            [1,2,3,4,5,6,7,8,9],
            ['#89cfa5','#f88e53','#FBF8B0','#FED281','#5e50a2','#4696b3','#9f0142','#db494a','#d5ee9f']
        )
        const projection = d3.geoTransverseMercator()
            .rotate([76 + 35 / 60, -40])
            .scale(6375)
            .translate([width / 2.60, height / 1.07])
        const svg = selection.select('svg').attr('width', width).attr('height', height)
            .on('mouseover', evt => findNeighbors(evt,null, k))

        const point = svg.selectAll(".point")
            .data(coordinates)
            .enter().append("circle")
            .attr("class", "point")
            .attr("cx", d => x(d[0]))
            .attr("cy", d => y(d[1]))
            .attr("r", 3)

        const cursor = svg.append("circle")
            .attr("id", "pt")
            .attr("r", 3)
            .attr("cx", width/2)
            .attr("cy", height/2)
            .style("fill", "black")

        listen(cursor)

        function drawSelection(quadtree, xx, yy, sel) {
            d3.selectAll('path').attr('fill', 'rgba(255,255,255,.15)')
            d3.selectAll('.location').remove()
            let pt, x, y
            pt = sel || d3.select("#pt");
            x = pt.attr('cx'), y = pt.attr('cy');
            //get nearest neighbours
            quadtree.root().mindist = 0;
            const bestqueue = new Array(quadtree.root()); // start with the root node of the quadtree
            const resultqueue = [];
            const scannedqueue = []; // only needed to visualise the work of the quadtree
            knearest(bestqueue, resultqueue, xx.invert(x), yy.invert(y), k, scannedqueue);

            resultqueue.forEach((d,i) => {
                return d.data.selection = true
            })
            scannedqueue.forEach((d) => d.length ? d.scanned=false : d.data.scanned = false)
            // paint
            svg.selectAll(".point").classed("scanned", (d) => d.scanned);
            svg.selectAll(".point").classed("selection", (d) => d.selection);
            // clean up data
            resultqueue.forEach((d) => {
                d.data.selection=false
            })
            return scannedqueue.forEach((d) => d.length ? d.scanned=false : d.data.scanned = false);
        }

        function findNeighbors (e, passPoint) {
            if (tab === 'zipcode') { return }
            let xy
            if(!passPoint) {
                xy = d3.pointer(e);
                svg.selectAll("#pt")
                    .attr("cx", xy[0])
                    .attr("cy", xy[1]);
                drawSelection(quadtree, x, y);
            }
            animateMap()
        }

        function animateMap (sel) {
            let selection = sel || d3.selectAll('.selection')
            let otherSvg = d3.select('#tristate-map svg g')
            var g = otherSvg.select('.places')
            selection.each((d,i) => {
                let fillColor
                var found = locations.find(o => o.zip_code == d[2] )
                if (found) {
                    fillColor = colorCluster(d[3])
                    otherSvg
                        .append('text')
                        .attr('font-size',1)
                        .attr('font-family','Avenir Next')
                        .style('text-shadow', '0 0 5px lightgrey, 0 0 5px grey, 0 0 7px grey, 0 0 10px darkgrey')
                        .attr('x',projection([found['longitude'],found['latitude']])[0])
                        .attr('y',projection([found['longitude'],found['latitude']])[1])
                        .attr('class','location')
                        // .text(found['city']
                            // + ' ' + found['zip_code']
                        // )
                }
                else { fillColor = 'none' }
                d3.select(`path#z-${d[2]}`).attr('fill',fillColor)
            })
        }

        function listen(pt) {
            const found = coordinates.find(o => o[2] == zip)
            if (found) {
                let x0 = x(found[0])
                let y0 = y(found[1])
                // const pt = d3.select("#pt")
                pt.attr('cx', x0).attr('cy', y0)
                drawSelection(quadtree, x, y, pt)
                animateMap(svg.selectAll(".point").filter('.selection'))
            }
        }
    }
    return chart
}
